import { FeatureGate } from "@/components/molecules/FeatureGate/FeatureGate";
import { useOverlay } from "@/overlay/overlays/default/ViewportOverlayProvider";
import { classNames } from "@/utility/jsx";
import type { GenericLayout } from "@mm/metx-workbench.meteomatics.com";
import type { ReactNode } from "react";
import { type HighlightType, useActiveGridHighlight } from "./GridCellHighlightProvider";
import { GridSortableElement } from "./GridSortableElement";

interface GridToolCellProps {
  layout: GenericLayout;
  disabled: boolean;
  onDrop: (sourceId: number, targetId: number) => void;
  /** Feature flag path that must be enabled for the tool to render. */
  featurePath: string;
  highlightType: HighlightType;
  toolId: number;
  /** Maps align their controls with header/footer, all other tools get extra padding instead. */
  variant: "map" | "plot";
  /** Rendered instead of the tool when the feature flag is disabled. */
  fallback: ReactNode;
  children: ReactNode;
}

export function GridToolCell({
  layout,
  disabled,
  onDrop,
  featurePath,
  highlightType,
  toolId,
  variant,
  fallback,
  children,
}: GridToolCellProps) {
  const { highlighted } = useActiveGridHighlight();
  const { showFooter, showHeader } = useOverlay();
  const { gridRowStart, gridRowEnd } = layout.gridCellLayout;
  const isHighlighted = !!highlighted && highlighted.type === highlightType && highlighted.id === toolId;

  const className =
    variant === "map"
      ? classNames(
          "grid__cell__map",
          // Check if grid cell is positioned on bottom
          gridRowEnd === 97 && "grid__cell__bottom",
          // Add class if footer is visible, so map can align controls
          showFooter && "footer-visible",
          isHighlighted && "highlighted",
        )
      : classNames(
          "grid__cell__plot",
          gridRowStart === 1 && showHeader && "extra-padding-top",
          gridRowEnd === 97 && showFooter && "extra-padding-bottom",
          isHighlighted && "highlighted",
        );

  return (
    <FeatureGate path={featurePath} fallback={fallback}>
      <GridSortableElement
        id={layout.id}
        disabled={disabled}
        gridCellLayout={layout.gridCellLayout}
        className={className}
        onDrop={onDrop}
      >
        {children}
      </GridSortableElement>
    </FeatureGate>
  );
}
